import {
  DurationPickerV2,
  FormLabel,
  InfoPopover,
  Select,
  Typography,
  useDurationPickerField,
} from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import {
  OptimisationStageInput,
  PlannerOptionsInput,
  WorkflowSplittingMethod,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  capitalize,
} from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { MaxComputationTimePicker } from './MaxComputationTimePicker';
import { MaxPlanningTimePicker } from './MaxPlanningTimePicker';
import { NumberBadge } from './NumberBadge';
import { OptimizationStagesList } from './OptimizationStagesList';
import { ValidatedNumberField } from './ValidatedNumberField';
import { getFieldErrorsForTasks } from '../../features/actions';
import {
  selectAdvancedStrategy,
  selectChunkifyOptions,
  selectFeasibilityStage,
  selectMxNOptions,
  selectOptimizationStages,
  selectOptimizationTarget,
  selectShowAdvancedPlannerOptions,
  selectWorkflowBatchNumber,
  selectWorkflowSplittingMethod,
} from '../../features/workflow/selectors';
import {
  setShowAdvancedPlannerOptions,
  updatePlannerOptions,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useValidBatches } from '../../hooks/useValidBatches';
import { PreviewBlocker } from '../../text';
import { useParametersForm } from '../../utils';
import { toPreviewBlockers } from '../../utils/previewBlockers';
import BatchesInput from '../BatchesInput';
import { AccordionParameterReference } from '../ModalAccordion/AccordionParameterReference';

type Props = {
  onChange?: (state: {
    batches: number | null;
    optimisationTarget: NonNullable<PlannerOptionsInput['optimisation_target']>;
    isParametersValid: boolean;
    isBatchValid: boolean;
    blockers: PreviewBlocker[];
    useLatestPlanAsHint: boolean;
  }) => void;
};

type OptimizationTarget =
  | 'minimize_total_plan_duration'
  | 'minimize_labware_idle_time'
  | 'balance_total_plan_duration_and_labware_idle_time';

const isOptimizationTarget = (value: string): value is OptimizationTarget => {
  return [
    'minimize_total_plan_duration',
    'minimize_labware_idle_time',
    'balance_total_plan_duration_and_labware_idle_time',
    'set_advanced_options',
  ].includes(value);
};

const isWorkflowSplittingMethod = (
  value: string
): value is WorkflowSplittingMethod => {
  return ['standard', 'strict_batch', 'strict_tree'].includes(value);
};

const WorkflowSplittingMethodPopover = (
  <div>
    <p>
      This is the method used to split the workflow for certain strategies that
      require splitting.
    </p>
    <ul>
      <li>
        <b>Strict Batch:</b> split the workflow by batch number.
      </li>
      <li>
        <b>Strict Tree:</b> Split the workflow by labware groups with no shared
        dependencies.
      </li>
      <li>
        <b>Standard:</b> hybrid approach between batches and trees, which tries
        to encapsulate all edge cases.
      </li>
    </ul>
  </div>
);

const OptimizationTargetPopover = (
  <div>
    <p>
      Choose how the solver should optimise your run once a valid plan is found.
    </p>
    <ul>
      <li>
        <b>Minimise total plan duration:</b> Finish the run as quickly as
        possible.
      </li>
      <li>
        <b>Minimise labware idle time:</b> Reduce how long labware sits idle at
        locations without an active task.
      </li>
      <li>
        <b>Balance duration and idle time:</b> Balance overall run time with
        time labware spends idle at locations without an active task.
      </li>
    </ul>
    <Typography variant="caption" color="text.secondary">
      Note: Optimisation targets are not applied when Advanced Options are
      selected.
    </Typography>
  </div>
);

export const PlanWorkflowForm = ({ onChange }: Props) => {
  const dispatch = useAppDispatch();
  const { minBatch, validBatches } = useValidBatches();
  const savedBatches = useAppSelector(selectWorkflowBatchNumber);
  const [batches, setBatches] = useState<number | null>(
    savedBatches ?? minBatch
  );
  const optimizationTarget =
    useAppSelector(selectOptimizationTarget) ?? 'minimize_total_plan_duration';
  const advancedStrategy = useAppSelector(selectAdvancedStrategy);
  const mxnOptions = useAppSelector(selectMxNOptions);
  const chunkifyOptions = useAppSelector(selectChunkifyOptions);
  const workflowSplittingMethod = useAppSelector(selectWorkflowSplittingMethod);
  const showAdvancedOptions = useAppSelector(selectShowAdvancedPlannerOptions);
  const feasibilityStage = useAppSelector(selectFeasibilityStage);
  const deepFeasibilitySolve = feasibilityStage?.deep_feasibility_solve ?? true;
  const optimizationStages = useAppSelector(selectOptimizationStages);
  const {
    isParametersValid,
    parameters,
    parametersWithoutFileType,
    handleSaveParameter,
  } = useParametersForm();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [blockers, setBlockers] = useState<PreviewBlocker[]>([]);
  const [useLatestPlanAsHint, setUseLatestPlanAsHint] = useState(false);
  const isPlanHintingEnabled = useFlag('fe.plan_hinting');

  useEffect(() => {
    if (savedBatches !== undefined && savedBatches !== null) {
      setBatches(savedBatches);
    }
  }, [savedBatches]);

  const isBatchValid = useMemo(
    () => batches !== null && validBatches.includes(batches ?? 0),
    [batches, validBatches]
  );

  useEffect(() => {
    const errors = dispatch(getFieldErrorsForTasks(parameters));

    if (!errors || errors.length === 0) {
      setBlockers([]);
    } else {
      setBlockers(toPreviewBlockers({ isError: true, errors }));
    }
  }, [dispatch, parameters]);

  useEffect(() => {
    onChange?.({
      batches,
      optimisationTarget: optimizationTarget,
      isParametersValid,
      isBatchValid,
      blockers,
      useLatestPlanAsHint,
    });
  }, [
    batches,
    optimizationTarget,
    isParametersValid,
    isBatchValid,
    blockers,
    useLatestPlanAsHint,
    onChange,
  ]);

  const { breakdown, handleBlur, handleUpdateTime } = useDurationPickerField(
    (newTotalSeconds: number) => {
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...feasibilityStage, // Preserve all existing feasibility_stage data
            advanced_strategy_options: {
              strategy: 'mxn',
              batches_for_initial_chunk:
                mxnOptions?.batches_for_initial_chunk ?? 1,
              initial_chunk_computation_time: newTotalSeconds,
            },
          },
        })
      );
    },
    mxnOptions?.initial_chunk_computation_time ?? 60
  );

  const handleOptimizationTargetChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;
    if (typeof value === 'string' && isOptimizationTarget(value)) {
      dispatch(
        updatePlannerOptions({
          optimisation_target: value,
        })
      );
    }
  };

  const handleShowAdvancedOptionsChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    dispatch(setShowAdvancedPlannerOptions(checked));

    // When advanced options are enabled, set optimization target to default
    if (checked) {
      const nextFeasibilityStage = {
        ...(feasibilityStage ?? {}),
        deep_feasibility_solve: deepFeasibilitySolve,
      };
      const updatedOptions: Partial<PlannerOptionsInput> = {
        optimisation_target: 'minimize_total_plan_duration',
        feasibility_stage: nextFeasibilityStage,
      };

      if (!optimizationStages || optimizationStages.length === 0) {
        updatedOptions.optimisation_stages = [
          {
            objective: 'makespan',
            stop_at: {
              max_computation_time: null,
              improvement_timer_limit: undefined,
              target_objective: null,
            },
            carry_forward_rule: 'plus_or_minus_10_percent',
          },
        ];
      }

      dispatch(updatePlannerOptions(updatedOptions));
    }
  };

  const handleAdvancedStrategyChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;

    if (value === '' || value === null) {
      // None selected - clear the advanced_strategy_options
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...(feasibilityStage ?? {}),
            advanced_strategy_options: undefined,
          },
        })
      );
    } else if (value === 'automatic') {
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...feasibilityStage, // Preserve all existing feasibility_stage data
            advanced_strategy_options: { strategy: 'automatic' },
          },
        })
      );
    } else if (value === 'chunkify') {
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...feasibilityStage, // Preserve all existing feasibility_stage data
            advanced_strategy_options: {
              strategy: 'chunkify',
              batches_per_chunk: chunkifyOptions?.batches_per_chunk ?? 1,
            },
          },
        })
      );
    } else if (value === 'mxn') {
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...feasibilityStage, // Preserve all existing feasibility_stage data
            advanced_strategy_options: {
              strategy: 'mxn',
              batches_for_initial_chunk:
                mxnOptions?.batches_for_initial_chunk ?? 1,
              initial_chunk_computation_time:
                mxnOptions?.initial_chunk_computation_time ?? 60,
            },
          },
        })
      );
    }
  };

  const handleDeepFeasibilitySolveChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    dispatch(
      updatePlannerOptions({
        feasibility_stage: {
          ...(feasibilityStage ?? {}),
          deep_feasibility_solve: checked,
        },
      })
    );
  };

  const handleMxNBatchesChange = (value: number) => {
    dispatch(
      updatePlannerOptions({
        feasibility_stage: {
          ...feasibilityStage, // Preserve all existing feasibility_stage data
          advanced_strategy_options: {
            strategy: 'mxn',
            batches_for_initial_chunk: value,
            initial_chunk_computation_time:
              mxnOptions?.initial_chunk_computation_time ?? 60,
          },
        },
      })
    );
  };

  const handleChunkifyBatchesChange = (value: number) => {
    dispatch(
      updatePlannerOptions({
        feasibility_stage: {
          ...feasibilityStage, // Preserve all existing feasibility_stage data
          advanced_strategy_options: {
            strategy: 'chunkify',
            batches_per_chunk: value,
          },
        },
      })
    );
  };

  const handleWorkflowSplittingMethodChange = (
    e: SelectChangeEvent<unknown>
  ) => {
    const value = e.target.value;
    if (typeof value === 'string' && isWorkflowSplittingMethod(value)) {
      dispatch(
        updatePlannerOptions({
          feasibility_stage: {
            ...feasibilityStage, // Preserve all existing feasibility_stage data
            workflow_splitting_method: value,
          },
        })
      );
    }
  };

  const handleAddOptimizationStage = (stage: OptimisationStageInput) => {
    const newStage = {
      ...stage,
    };

    dispatch(
      updatePlannerOptions({
        optimisation_stages: [...optimizationStages, newStage],
      })
    );
  };

  const handleEditOptimizationStage = (
    id: string,
    updatedStage: OptimisationStageInput
  ) => {
    const targetIndex = parseInt(id, 10);

    if (targetIndex >= 0 && targetIndex < optimizationStages.length) {
      const updatedStages = [...optimizationStages];
      updatedStages[targetIndex] = {
        ...updatedStages[targetIndex],
        ...updatedStage,
      };

      dispatch(
        updatePlannerOptions({
          optimisation_stages: updatedStages,
        })
      );
    }
  };

  const handleDeleteOptimizationStage = (id: string) => {
    const targetIndex = parseInt(id, 10);

    if (targetIndex >= 0 && targetIndex < optimizationStages.length) {
      const filteredStages = optimizationStages.filter(
        (_, index) => index !== targetIndex
      );

      dispatch(
        updatePlannerOptions({
          optimisation_stages: filteredStages,
        })
      );
    }
  };

  const handleReorderOptimizationStages = (reorderedStages: any[]) => {
    dispatch(
      updatePlannerOptions({
        optimisation_stages: reorderedStages,
      })
    );
  };

  return (
    <Stack spacing={2} p={2}>
      <Stack spacing={2}>
        <BatchesInput handleChange={setBatches} batches={batches} />
      </Stack>

      <Divider />

      <Stack spacing={2}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="Planning Configuration" />
          <Tab label="Parameters" />
        </Tabs>

        {activeTab === 0 && (
          <Stack spacing={2} paddingY={1}>
            <MaxPlanningTimePicker />

            {isPlanHintingEnabled && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useLatestPlanAsHint}
                    onChange={(e) => setUseLatestPlanAsHint(e.target.checked)}
                  />
                }
                label={
                  <FormLabel
                    infoPopover={
                      <InfoPopover
                        title="Use latest plan as hint"
                        copy="When enabled, the most recently completed plan will be used as a starting hint for the solver. This can speed up planning for workflows that have been planned before."
                      />
                    }
                  >
                    <Typography variant="body1" textTransform={'none'}>
                      Use latest plan as hint
                    </Typography>
                  </FormLabel>
                }
              />
            )}

            <Select
              value={
                showAdvancedOptions
                  ? 'minimize_total_plan_duration'
                  : optimizationTarget
              }
              onChange={handleOptimizationTargetChange}
              label="Optimization target"
              disabled={showAdvancedOptions}
              sx={{ opacity: showAdvancedOptions ? 0.5 : 1 }}
              infoPopover={
                <InfoPopover
                  title="Optimisation Target"
                  copy={OptimizationTargetPopover}
                />
              }
            >
              <MenuItem value="minimize_total_plan_duration">
                Minimise total plan duration
              </MenuItem>
              <MenuItem value="minimize_labware_idle_time">
                Minimise labware idle time
              </MenuItem>
              <MenuItem value="balance_total_plan_duration_and_labware_idle_time">
                Balance duration and idle time
              </MenuItem>
            </Select>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showAdvancedOptions}
                  onChange={handleShowAdvancedOptionsChange}
                />
              }
              label="Advanced options"
            />

            {showAdvancedOptions && (
              <>
                <Stack direction="row" gap={1} alignItems="center">
                  <NumberBadge value={1} />
                  <Typography variant="body1" color="textSecondary">
                    First valid solution
                  </Typography>
                </Stack>

                <Select
                  value={advancedStrategy ?? ''}
                  onChange={handleAdvancedStrategyChange}
                  label="Advanced strategies"
                  infoPopover={
                    <InfoPopover
                      title="Advanced strategies"
                      copy={
                        <Typography variant="body2">
                          These strategies determine how the system finds the
                          first valid solution for your plan. Different
                          strategies are suited to different workflow
                          structures. If you’re unsure which to use, contact
                          Automata Support.
                          <ul>
                            <li>
                              <b>None:</b> Uses standard planning strategies.
                            </li>
                            <li>
                              <b>Automatic:</b> Automatically selects advanced
                              settings optimized for your workflow.
                            </li>
                            <li>
                              <b>MxN:</b> Best suited for workflows where the
                              same process is repeated identically multiple
                              times.
                            </li>
                            <li>
                              <b>Chunkify:</b> Best suited for complex workflows
                              which are not exactly repeated.
                            </li>
                          </ul>
                        </Typography>
                      }
                    />
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="automatic">Automatic</MenuItem>
                  <MenuItem value="mxn">MxN</MenuItem>
                  <MenuItem value="chunkify">Chunkify</MenuItem>
                </Select>

                {advancedStrategy === 'mxn' && mxnOptions && (
                  <Stack gap={2}>
                    <ValidatedNumberField
                      label="Batches for initial chunk"
                      value={mxnOptions.batches_for_initial_chunk}
                      onValidChange={handleMxNBatchesChange}
                      min={1}
                    />
                    <DurationPickerV2
                      label="Initial chunk computation time"
                      hours={breakdown.hours}
                      minutes={breakdown.minutes}
                      seconds={breakdown.seconds}
                      handleBlurParent={handleBlur}
                      handleUpdateTime={handleUpdateTime}
                    />
                  </Stack>
                )}

                {advancedStrategy === 'chunkify' && chunkifyOptions && (
                  <Stack gap={2}>
                    <ValidatedNumberField
                      label="Batches per chunk"
                      value={chunkifyOptions.batches_per_chunk}
                      onValidChange={handleChunkifyBatchesChange}
                      min={1}
                    />
                  </Stack>
                )}

                {(advancedStrategy === 'mxn' ||
                  advancedStrategy === 'chunkify') && (
                  <Select
                    value={workflowSplittingMethod ?? 'standard'}
                    onChange={handleWorkflowSplittingMethodChange}
                    label="Workflow splitting method"
                    infoPopover={
                      <InfoPopover
                        title="Workflow Splitting Method"
                        copy={WorkflowSplittingMethodPopover}
                      />
                    }
                  >
                    <MenuItem
                      value="standard"
                      title="hybrid approach between batches and trees, which tries to encapsulate all edge cases."
                    >
                      Standard
                    </MenuItem>
                    <MenuItem
                      value="strict_batch"
                      title="split the workflow by batch number."
                    >
                      Strict Batch
                    </MenuItem>
                    <MenuItem
                      value="strict_tree"
                      title="Split the workflow by labware groups with no shared dependencies."
                    >
                      Strict Tree
                    </MenuItem>
                  </Select>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={deepFeasibilitySolve}
                      onChange={handleDeepFeasibilitySolveChange}
                    />
                  }
                  label={
                    <FormLabel
                      infoPopover={
                        <InfoPopover
                          title="Deep feasibility solve"
                          copy="Whether to perform a deep feasibility solve as a part of the feasibility stage. <br/><br/> For some workflows, this is the only way to find a feasible solution. For others, it may cause longer planning times without significant benefit."
                        ></InfoPopover>
                      }
                    >
                      <Typography variant="body1" textTransform={'none'}>
                        Deep feasibility solve
                      </Typography>
                    </FormLabel>
                  }
                />

                <MaxComputationTimePicker />

                <OptimizationStagesList
                  stages={optimizationStages.map((stage, index) => {
                    return {
                      ...stage,
                      id: index.toString(),
                      name: `Stage ${index + 1}`,
                      __reorder__: `#${index + 1} ${capitalize(
                        stage.objective ?? ''
                      )}`,
                    };
                  })}
                  onReorderStages={handleReorderOptimizationStages}
                  onAddStage={handleAddOptimizationStage}
                  onEditStage={handleEditOptimizationStage}
                  onDeleteStage={handleDeleteOptimizationStage}
                />
              </>
            )}
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack spacing={1} paddingY={1}>
            {parametersWithoutFileType?.length === 0 ? (
              <Typography variant="body2">
                There are no parameters set for this workflow.
              </Typography>
            ) : (
              <>
                {parameters.map((param) => (
                  <AccordionParameterReference
                    key={param.id}
                    parameter={param}
                    changeParameter={handleSaveParameter}
                  />
                ))}
                {parameters.length > parametersWithoutFileType.length && (
                  <Typography paddingTop={1} variant="subtitle2">
                    File type parameters are not displayed for preview/planning.
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PlanWorkflowForm;
